import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress } from '@mui/material';
import config from '../config/config';
import DOMPurify from 'dompurify';

interface PageData {
  title: string;
  content: string;
}

const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${config.api.baseUrl}/cms/pages/${slug}`);
        if (!response.ok) {
          throw new Error('Sidan kunde inte hittas');
        }
        const data = await response.json();
        setPage(data);
      } catch (error) {
        setError('Ett fel uppstod när sidan skulle laddas');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !page) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography color="error">{error || 'Sidan kunde inte hittas'}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {page.title}
      </Typography>
      <Box
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(page.content)
        }}
        sx={{
          '& img': {
            maxWidth: '100%',
            height: 'auto'
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            my: 2
          },
          '& th, & td': {
            border: '1px solid #ddd',
            padding: 2
          }
        }}
      />
    </Container>
  );
};

export default DynamicPage; 